/**
 * Frontend validation utilities for biometric parameters
 * This mirrors the backend validation but is optimized for client-side use
 */

/**
 * Validation rules for biometric parameters
 */
const biometricValidationRules = {
  // Cognition parameters
  'tmtA_time': { min: 0, max: 300, dataType: 'number', required: false },
  'tmtA_errors': { min: 0, max: 50, dataType: 'integer', required: false },
  'tmtB_time': { min: 0, max: 600, dataType: 'number', required: false },
  'tmtB_errors': { min: 0, max: 50, dataType: 'integer', required: false },
  'wordRecall_immediate1': { min: 0, max: 10, dataType: 'integer', required: false },
  'wordRecall_immediate2': { min: 0, max: 10, dataType: 'integer', required: false },
  'wordRecall_delayed': { min: 0, max: 10, dataType: 'integer', required: false },
  'stroop_word_time': { min: 0, max: 300, dataType: 'number', required: false },
  'stroop_word_errors': { min: 0, max: 50, dataType: 'integer', required: false },
  'stroop_color_time': { min: 0, max: 300, dataType: 'number', required: false },
  'stroop_color_errors': { min: 0, max: 50, dataType: 'integer', required: false },
  'stroop_interference_time': { min: 0, max: 300, dataType: 'number', required: false },
  'stroop_interference_errors': { min: 0, max: 50, dataType: 'integer', required: false },

  // Vision parameters (using standardized parameter names)
  'biometrics_vision_visualAcuity_distance_right': {
    dataType: 'string',
    pattern: /^20\/\d{2,3}$|^CF|^HM|^LP|^NLP$/i,
    required: false,
    customValidator: 'snellen'
  },
  'biometrics_vision_visualAcuity_distance_left': {
    dataType: 'string',
    pattern: /^20\/\d{2,3}$|^CF|^HM|^LP|^NLP$/i,
    required: false,
    customValidator: 'snellen'
  },
  'biometrics_vision_visualAcuity_distance_both': {
    dataType: 'string',
    pattern: /^20\/\d{2,3}$|^CF|^HM|^LP|^NLP$/i,
    required: false,
    customValidator: 'snellen'
  },
  'biometrics_vision_visualAcuity_near_right': {
    dataType: 'string',
    pattern: /^J\d{1,2}$|^J\d{1,2}\+$/i,
    required: false,
    customValidator: 'jaeger'
  },
  'biometrics_vision_visualAcuity_near_left': {
    dataType: 'string',
    pattern: /^J\d{1,2}$|^J\d{1,2}\+$/i,
    required: false,
    customValidator: 'jaeger'
  },
  'biometrics_vision_visualAcuity_near_both': {
    dataType: 'string',
    pattern: /^J\d{1,2}$|^J\d{1,2}\+$/i,
    required: false,
    customValidator: 'jaeger'
  },
  'biometrics_vision_visualAcuity_pinhole_right': {
    dataType: 'string',
    pattern: /^20\/\d{2,3}$|^CF|^HM|^LP|^NLP$/i,
    required: false,
    customValidator: 'snellen'
  },
  'biometrics_vision_visualAcuity_pinhole_left': {
    dataType: 'string',
    pattern: /^20\/\d{2,3}$|^CF|^HM|^LP|^NLP$/i,
    required: false,
    customValidator: 'snellen'
  },

  // Strength parameters
  'handgrip_right_1': { min: 0, max: 100, dataType: 'number', required: false },
  'handgrip_right_2': { min: 0, max: 100, dataType: 'number', required: false },
  'handgrip_right_3': { min: 0, max: 100, dataType: 'number', required: false },
  'handgrip_left_1': { min: 0, max: 100, dataType: 'number', required: false },
  'handgrip_left_2': { min: 0, max: 100, dataType: 'number', required: false },
  'handgrip_left_3': { min: 0, max: 100, dataType: 'number', required: false },

  // Biomeasurement parameters
  'height': { min: 50, max: 250, dataType: 'number', required: false },
  'weight': { min: 20, max: 300, dataType: 'number', required: false },
  'waistCircumference': { min: 40, max: 200, dataType: 'number', required: false },
  'hipCircumference': { min: 40, max: 200, dataType: 'number', required: false },
  'bodyFatPercentage': { min: 0, max: 60, dataType: 'number', required: false },
  'bodyFatMass': { min: 0, max: 150, dataType: 'number', required: false },
  'leanBodyMass': { min: 0, max: 150, dataType: 'number', required: false },
  'muscleMass': { min: 0, max: 150, dataType: 'number', required: false },
  'boneMass': { min: 0, max: 20, dataType: 'number', required: false },
  'visceralFatRating': { min: 1, max: 30, dataType: 'integer', required: false },
  'metabolicAge': { min: 12, max: 90, dataType: 'integer', required: false },

  // Flexibility parameters
  'sitAndReach': { min: -20, max: 60, dataType: 'number', required: false },
  'shoulderFlexibility_right': { min: 0, max: 180, dataType: 'number', required: false },
  'shoulderFlexibility_left': { min: 0, max: 180, dataType: 'number', required: false },

  // Balance parameters
  'singleLegStance_right': { min: 0, max: 300, dataType: 'number', required: false },
  'singleLegStance_left': { min: 0, max: 300, dataType: 'number', required: false },

  // Endurance parameters
  'vo2Max': { min: 10, max: 80, dataType: 'number', required: false },
  'restingHeartRate': { min: 30, max: 120, dataType: 'integer', required: false },
  'maxHeartRate': { min: 100, max: 220, dataType: 'integer', required: false },

  // Power parameters
  'jump_best': { min: 0, max: 100, dataType: 'number', required: false },
  'jump_percent_max': { min: 0, max: 100, dataType: 'number', required: false },
  'jump_average_height': { min: 0, max: 100, dataType: 'number', required: false },
  'power_watts': { min: 0, max: 5000, dataType: 'number', required: false },

  // Pulmonary parameters
  'fev1': { min: 0, max: 10, dataType: 'number', required: false },
  'fvc': { min: 0, max: 10, dataType: 'number', required: false },
  'pefr': { min: 0, max: 1000, dataType: 'number', required: false },
  'fev1_predicted': { min: 0, max: 10, dataType: 'number', required: false },
  'fvc_predicted': { min: 0, max: 10, dataType: 'number', required: false },
  'pefr_predicted': { min: 0, max: 1000, dataType: 'number', required: false },
  'fef25_75': { min: 0, max: 20, dataType: 'number', required: false },
  'fev1_fvc_ratio': { min: 0, max: 100, dataType: 'number', required: false },
  'lung_age_estimate': { min: 0, max: 150, dataType: 'number', required: false }
};

/**
 * Custom validator for Snellen visual acuity values
 */
function validateSnellenValue(value) {
  if (typeof value !== 'string') return false;
  
  const upperValue = value.toUpperCase();
  
  // Special values
  if (['CF', 'HM', 'LP', 'NLP'].includes(upperValue)) {
    return true;
  }
  
  // Standard Snellen format: 20/XX
  const snellenMatch = upperValue.match(/^20\/(\d{2,3})$/);
  if (snellenMatch) {
    const denominator = parseInt(snellenMatch[1], 10);
    return denominator >= 10 && denominator <= 400;
  }
  
  return false;
}

/**
 * Custom validator for Jaeger visual acuity values
 */
function validateJaegerValue(value) {
  if (typeof value !== 'string') return false;
  
  const upperValue = value.toUpperCase();
  const jaegerMatch = upperValue.match(/^J(\d{1,2})(\+)?$/);
  
  if (jaegerMatch) {
    const number = parseInt(jaegerMatch[1], 10);
    return number >= 1 && number <= 20;
  }
  
  return false;
}

/**
 * Gets validation rules for frontend form components (Ant Design)
 * @param {string} parameterKey - Raw parameter key
 * @returns {Array} - Array of Ant Design form validation rules
 */
export function getFormValidationRules(parameterKey) {
  const rules = biometricValidationRules[parameterKey];
  if (!rules) return [];
  
  const formRules = [];
  
  if (rules.required) {
    formRules.push({ required: true, message: `Please enter ${parameterKey}` });
  }
  
  if (rules.dataType === 'number' || rules.dataType === 'integer') {
    if (rules.min !== undefined || rules.max !== undefined) {
      formRules.push({
        validator: (_, value) => {
          if (value === null || value === undefined || value === '') return Promise.resolve();
          const numValue = parseFloat(value);
          if (isNaN(numValue)) {
            return Promise.reject(new Error('Please enter a valid number'));
          }
          if (rules.min !== undefined && numValue < rules.min) {
            return Promise.reject(new Error(`Value must be at least ${rules.min}`));
          }
          if (rules.max !== undefined && numValue > rules.max) {
            return Promise.reject(new Error(`Value must be no more than ${rules.max}`));
          }
          return Promise.resolve();
        }
      });
    }
  }
  
  if (rules.pattern) {
    formRules.push({
      pattern: rules.pattern,
      message: 'Please enter a valid format'
    });
  }
  
  if (rules.customValidator === 'snellen') {
    formRules.push({
      validator: (_, value) => {
        if (value === null || value === undefined || value === '') return Promise.resolve();
        if (validateSnellenValue(value)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid Snellen value (e.g., 20/20, CF, HM, LP, NLP)'));
      }
    });
  }
  
  if (rules.customValidator === 'jaeger') {
    formRules.push({
      validator: (_, value) => {
        if (value === null || value === undefined || value === '') return Promise.resolve();
        if (validateJaegerValue(value)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid Jaeger value (e.g., J1, J2+)'));
      }
    });
  }
  
  return formRules;
}

/**
 * Gets validation metadata for a parameter (for display purposes)
 * @param {string} parameterKey - Raw parameter key
 * @returns {Object|null} - Validation metadata or null if not found
 */
export function getValidationMetadata(parameterKey) {
  const rules = biometricValidationRules[parameterKey];
  if (!rules) return null;
  
  const metadata = {
    required: rules.required || false,
    dataType: rules.dataType,
    hasRange: rules.min !== undefined || rules.max !== undefined,
    min: rules.min,
    max: rules.max,
    hasPattern: !!rules.pattern,
    hasCustomValidation: !!rules.customValidator
  };
  
  // Generate helpful placeholder text
  if (rules.dataType === 'string' && rules.customValidator === 'snellen') {
    metadata.placeholder = 'e.g., 20/20, 20/40, CF, HM';
  } else if (rules.dataType === 'string' && rules.customValidator === 'jaeger') {
    metadata.placeholder = 'e.g., J1, J2, J3+';
  } else if (rules.min !== undefined && rules.max !== undefined) {
    metadata.placeholder = `${rules.min} - ${rules.max}`;
  } else if (rules.min !== undefined) {
    metadata.placeholder = `≥ ${rules.min}`;
  } else if (rules.max !== undefined) {
    metadata.placeholder = `≤ ${rules.max}`;
  }
  
  return metadata;
}

/**
 * Validates biometric data on the client side
 * @param {Object} biometricData - Object with parameter keys and values
 * @returns {Object} - { isValid: boolean, errors: Object }
 */
export function validateBiometricData(biometricData) {
  const allErrors = {};
  let hasErrors = false;
  
  for (const [key, value] of Object.entries(biometricData)) {
    const rules = biometricValidationRules[key];
    if (!rules) continue;
    
    const errors = [];
    
    // Check if required
    if (rules.required && (value === null || value === undefined || value === '')) {
      errors.push(`${key} is required`);
    }
    
    // Skip validation for empty optional fields
    if (!rules.required && (value === null || value === undefined || value === '')) {
      continue;
    }
    
    // Data type validation
    if (rules.dataType === 'number' || rules.dataType === 'integer') {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        errors.push(`${key} must be a valid number`);
      } else {
        // Range validation
        if (rules.min !== undefined && numValue < rules.min) {
          errors.push(`${key} must be at least ${rules.min}`);
        }
        if (rules.max !== undefined && numValue > rules.max) {
          errors.push(`${key} must be no more than ${rules.max}`);
        }
        
        // Integer validation
        if (rules.dataType === 'integer' && !Number.isInteger(numValue)) {
          errors.push(`${key} must be a whole number`);
        }
      }
    }
    
    // Pattern validation
    if (rules.pattern && typeof value === 'string' && !rules.pattern.test(value)) {
      errors.push(`${key} format is invalid`);
    }
    
    // Custom validation
    if (rules.customValidator === 'snellen' && !validateSnellenValue(value)) {
      errors.push(`${key} must be a valid Snellen value`);
    }
    if (rules.customValidator === 'jaeger' && !validateJaegerValue(value)) {
      errors.push(`${key} must be a valid Jaeger value`);
    }
    
    if (errors.length > 0) {
      allErrors[key] = errors;
      hasErrors = true;
    }
  }
  
  return {
    isValid: !hasErrors,
    errors: allErrors
  };
}
